import * as React from "react";
import CustomFieldAPI from "./CustomFieldAPI";
import { Validation } from "src/common/utils";
import { RenderedCustomField } from "src/components/forms/RenderCustomFields";
import { Spinner } from "src/components/common";

export type ValidationStatus =
	| "success"
	| "error"
	| "warning"
	| "validating"
	| undefined;

export interface ICustomFieldValidation {
	fieldId?: number;
	name: string;
	validationStatus: ValidationStatus;
	message: string;
}

export interface ICustomFieldTypeAndId {
	typeOfCustomFieldValue:
	| "VitalsType"
	| "CoreType"
	| "AssessmentType"
	| "ClientType"
	| "CareType";
	value: number;
}

export interface ICustomFieldContainerProps {
	organizationId: number;
	pickListItems: PickListItemResponseDTO[];
	datasetId: number;
	customFieldTypeIds?: ICustomFieldTypeAndId;
	customFieldTypes: [
		"ClientType" | "AssessmentType" | "VitalsType" | "CoreType" | "CareType"
	];
	getCustomFieldValueFromState: (value: CustomFieldValueDTO[]) => void;
	getCustomFieldValidation: (value: ICustomFieldValidation[]) => void;
	getCustomFields?: (value: CustomFieldDTO[]) => void;
}

export interface IRenderCustomFieldData {
	id: number | undefined;
	fieldId: number;
	name: string;
	value: string;
	options: string[];
	type: string;
	required: boolean;
	placeholderText: string;
	validation: ICustomFieldValidation;
	responsive: boolean;
	onChangeCustomFieldValue: (e: any, id?: number) => void;
}

interface IState {
	customFieldValues: CustomFieldValueDTO[];
	customFieldToRender: IRenderCustomFieldData[];
	customFields: CustomFieldDTO[];
	customFieldValidation: ICustomFieldValidation[];
	isLoading: boolean;
}

export class CustomFieldContainer extends React.Component<
	ICustomFieldContainerProps,
	IState
	> {
	constructor(props: ICustomFieldContainerProps) {
		super(props);
		this.state = {
			customFieldValidation: [],
			customFieldToRender: [],
			customFields: [],
			customFieldValues: [],
			isLoading: true,
		};
	}

	public componentDidMount() {
		CustomFieldAPI.getOrganizationCustomFields(
			+this.props.organizationId,
			this.props.datasetId
		)
			.then((orgCustomFields) => {
				const customFields = this.getCustomFieldsMatchingDataset(
					orgCustomFields
				);
				this.setState({ customFields });
			})
			.then(() => {
				if (this.props.customFieldTypeIds) {
					const customFieldTypeIds = { ...this.props.customFieldTypeIds }; // need better naming for prop
					CustomFieldAPI.getClientSiteCustomFieldValues({
						datasetId: this.props.datasetId,
						customFieldValueType: customFieldTypeIds.typeOfCustomFieldValue,
						customFieldValueId: customFieldTypeIds.value,
					}).then((customFieldValues) => {
						if (
							customFieldValues !== undefined &&
							customFieldValues.length > 0
						) {
							// find any new customfields that dont match any queried custom field values
							const fieldIdsOfExistingCustomFieldValues = customFieldValues.map(
								(cfv) => cfv.fieldId
							);
							const filteredCustomFields = this.state.customFields.filter(
								(cf) => !fieldIdsOfExistingCustomFieldValues.includes(cf.id)
							);

							if (filteredCustomFields.length > 0) {
								const newCustomFieldValues = filteredCustomFields.map(
									(cf) =>
										({
											id: undefined,
											name: cf.name,
											fieldId: cf.id,
											value: "",
										} as CustomFieldValueDTO)
								);

								customFieldValues.concat(newCustomFieldValues);
							}

							const customFieldValueValidation = Validation.validateNewCustomFields(
								this.state.customFields,
								customFieldValues
							);
							const createCustomFieldDataToRender = this.createCustomFieldDataToRender(
								customFieldValueValidation,
								customFieldValues,
								this.state.customFields
							);
							this.setState(
								{
									customFieldValues,
									customFieldValidation: customFieldValueValidation,
									customFieldToRender: createCustomFieldDataToRender,
								},
								() => {
									this.props.getCustomFieldValueFromState(customFieldValues);
									this.props.getCustomFieldValidation(
										customFieldValueValidation
									);
								}
							);
						} else {
							const customFieldValuesFromCustomFields = this.state.customFields.map(
								(cf) =>
									({
										id: undefined,
										name: cf.name,
										value: "",
										fieldId: cf.id,
									} as CustomFieldValueDTO)
							);

							const validation = Validation.validateNewCustomFields(
								this.state.customFields,
								customFieldValuesFromCustomFields
							);
							const createCustomFieldDataToRender = this.createCustomFieldDataToRender(
								validation,
								customFieldValuesFromCustomFields,
								this.state.customFields
							);
							this.setState(
								{
									customFieldValues: customFieldValuesFromCustomFields,
									customFieldValidation: validation,
									customFieldToRender: createCustomFieldDataToRender,
								},
								() => {
									this.props.getCustomFieldValueFromState(
										customFieldValuesFromCustomFields
									);
									this.props.getCustomFieldValidation(validation);
								}
							);
						}
					});
				} else {
					const customFieldValuesFromCustomFields = this.state.customFields.map(
						(cf) =>
							({
								id: undefined,
								name: cf.name,
								value: "",
								fieldId: cf.id,
							} as CustomFieldValueDTO)
					);

					const validation = Validation.validateNewCustomFields(
						this.state.customFields,
						customFieldValuesFromCustomFields
					);
					const createCustomFieldDataToRender = this.createCustomFieldDataToRender(
						validation,
						customFieldValuesFromCustomFields,
						this.state.customFields
					);
					this.setState(
						{
							customFieldValues: customFieldValuesFromCustomFields,
							customFieldValidation: validation,
							customFieldToRender: createCustomFieldDataToRender,
						},
						() => {
							this.props.getCustomFieldValueFromState(
								customFieldValuesFromCustomFields
							);
							this.props.getCustomFieldValidation(validation);
						}
					);
				}
			})
			.then(() => this.setState({ isLoading: false }));
	}

	public render() {
		return this.state.isLoading !== false ? (
			<Spinner />
		) : (
				this.state.customFieldToRender.map((d) => (
					<RenderedCustomField customFieldData={d} key={d.name} />
				))
			);
	}

	private isEmptyOrUndefined = (value: any) =>
		value === undefined || value.length === 0;

	private getDisplayTypes = (types: string[]) => {
		const customFieldTypes: number[] = [];
		types.forEach((type) => {
			const foundPickListItem = this.props.pickListItems.find(
				(pItem: PickListItemResponseDTO) => pItem.code === type
			) as PickListItemResponseDTO;
			if (foundPickListItem) {
				customFieldTypes.push(foundPickListItem.id!);
			}
		});

		return customFieldTypes;
	};

	private getCustomFieldsMatchingDataset = (customFields: CustomFieldDTO[]) => {
		const types = this.getDisplayTypes(this.props.customFieldTypes);
		const fields = customFields
			.filter(
				(cf) =>
					cf.datasetIds!.includes(this.props.datasetId) &&
					types.includes(cf.customFieldType!)
			)
			.sort((a, b) => a.viewOrder! - b.viewOrder!);
		return fields;
	};

	private createCustomFieldDataToRender = (
		validation: ICustomFieldValidation[],
		customFieldValues: CustomFieldValueDTO[],
		customFields: CustomFieldDTO[]
	) => {
		const dataToRender: IRenderCustomFieldData[] = [];
		customFieldValues.forEach((cfv) => {
			let options: string[] = [];
			let controlType: string | undefined = "";
			let foundValidation: ICustomFieldValidation = {
				name: cfv.name!,
				validationStatus: undefined,
				message: "",
			};

			const foundCustomFieldData = customFields.find(
				(cf) => cf.id === cfv.fieldId
			);

			if (foundCustomFieldData !== undefined) {
				if (
					foundCustomFieldData!.options !== undefined &&
					foundCustomFieldData.options.length > 0
				) {
					options = this.translateOptions(foundCustomFieldData!.options!);
				}
				controlType = this.getControlType(foundCustomFieldData!.controlTypeId!);
				foundValidation = validation.find(
					(v) => v.name === foundCustomFieldData!.name
				)!;

				dataToRender.push({
					id: cfv.id,
					name: foundCustomFieldData!.name!,
					options,
					placeholderText: foundCustomFieldData!.placeholderText,
					value: cfv.value,
					type: controlType,
					fieldId: foundCustomFieldData!.id,
					required: foundCustomFieldData!.required,
					validation: foundValidation,
					responsive: true,
					onChangeCustomFieldValue: this.customFieldOnChange,
				} as IRenderCustomFieldData);
			} else {
				return;
			}
		});

		return dataToRender;
	};

	private customFieldOnChange = (customFieldValueId: number, e: any) => {
		// figure out type here
		let foundValue = {} as CustomFieldValueDTO;
		let foundValueIndex = undefined as number | undefined;
		let foundValueIndexByName = undefined as number | undefined;
		const updatedCustomFieldValues = this.state.customFieldValues;

		if (customFieldValueId === undefined) {
			// update customfield value by name
			foundValue = this.state.customFieldValues.find(
				(cf) => cf.name === e.target.name
			) as CustomFieldValueDTO;
			foundValueIndexByName = this.state.customFieldValues.findIndex(
				(cf) => cf.name! === e.target.name
			) as number;
		} else {
			foundValue = this.state.customFieldValues.find(
				(cf) => cf.id === customFieldValueId
			) as CustomFieldValueDTO;
			foundValueIndex = this.state.customFieldValues.findIndex(
				(cf) => cf.id === customFieldValueId
			) as number;
		}

		if (Object.entries(foundValue).length > 0) {
			// initialized found value as an empty object if we found one from state itll have entries
			foundValue.value = e.target.value;
			if (foundValueIndex !== undefined) {
				updatedCustomFieldValues[foundValueIndex] = foundValue;
			} else {
				updatedCustomFieldValues[foundValueIndexByName!] = foundValue;
			}

			// update renderDataValue value and validation
			const foundRenderedData = this.state.customFieldToRender.find(
				(cfvRender) => cfvRender.name === foundValue.name
			);
			const foundRenderedDataIndex = this.state.customFieldToRender.findIndex(
				(cfvRender) => cfvRender.name === foundValue.name
			);
			if (foundRenderedData) {
				foundRenderedData.value = foundValue.value!;
				foundRenderedData.validation = this.getSingleCustomFieldValueValidation(
					updatedCustomFieldValues,
					foundValue.name!,
					foundValue.fieldId
				);
			}

			const updatedCustomFieldValuesToRender = this.state.customFieldToRender;
			updatedCustomFieldValuesToRender[
				foundRenderedDataIndex
			] = foundRenderedData!;

			this.setState(
				{
					customFieldValues: updatedCustomFieldValues,
					customFieldToRender: updatedCustomFieldValuesToRender,
				},
				() => {
					this.props.getCustomFieldValueFromState(updatedCustomFieldValues);
					this.props.getCustomFieldValidation(
						updatedCustomFieldValuesToRender.map((c) => c.validation)
					);
				}
			);
		} else {
			return;
		}
	};

	private getControlType = (pickListId: number) => {
		const item = this.props.pickListItems.find(
			(pItem: PickListItemResponseDTO) => pItem.id === pickListId
		);
		return this.isEmptyOrUndefined(item) ? "" : item!.code;
	};

	private translateOptions = (options?: CustomFieldOptionsData[]) => {
		if (this.isEmptyOrUndefined(options)) {
			return [];
		}
		// translate to array of strings.
		const newOptions = options!.map((opt) => opt.options!);
		return newOptions;
	};

	private getSingleCustomFieldValueValidation = (
		updatedCustomFieldValues: CustomFieldValueDTO[],
		fieldName: string,
		fieldId?: number
	) => {
		const updatedValdiation = Validation.validateNewCustomFields(
			this.state.customFields,
			updatedCustomFieldValues
		); // may have to rethink logic here
		const findUpdatedCustomFieldValidation = updatedValdiation.find(
			(val) => val.fieldId! === fieldId || val.name === fieldName
		) as ICustomFieldValidation;

		return findUpdatedCustomFieldValidation;
	};
}

export const getCustomFieldValues = (
	customFieldValues: CustomFieldValueDTO[]
) => {
	return customFieldValues;
};

export const getCustomFieldValidation = (
	customFieldValidation: ICustomFieldValidation[]
) => {
	return customFieldValidation;
};

export const getCustomFields = (customFields: CustomFieldDTO[]) => {
	return customFields;
};

export const findCustomFieldValueToDisplay = (
	customFieldValues: CustomFieldValueDTO[],
	customFieldName: string
) => {
	if (customFieldValues !== undefined && customFieldValues.length > 0) {
		const foundValue = customFieldValues.find(
			(v) => v.name! === customFieldName
		);
		return !foundValue ? foundValue!.value : "";
	}

	return "";
};
